import { createConversation } from 'api/v2/AI';
import last from 'lodash/last';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationQuery } from 'types/ai.types';
import {
  AIAgentEnum,
  AIRoleEnum,
  type ConversationMessage,
} from 'types/ai.types';
import { AIUsageConsentType } from 'types/users.types';
import { ONE_SECOND } from 'utils/time';
import tryCatch from 'utils/tryCatch';

const STORAGE_KEY = 'ai_deductibility_suggestions_2024';

const getCachedDataStorage = (totalDeductibleAmount: number) =>
  tryCatch(() => {
    const cachedData = localStorage.getItem(STORAGE_KEY);
    const parsedCache = cachedData ? JSON.parse(cachedData) : null;

    if (parsedCache?.totalDeductibleAmount === totalDeductibleAmount) {
      return parsedCache.content.split('\n');
    }

    return null;
  });

const useDudictibilitySuggestions = (
  totalDeductibleAmount: number,
  consent: AIUsageConsentType,
  containerRef: React.RefObject<HTMLDivElement>,
) => {
  const [data, setData] = useState<string[]>([]);
  const [streamedData, setStreamedData] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fakeStreamWordIndexRef = useRef(0);
  const [streamDone, setStreamDone] = useState(false);

  const { t } = useTranslation();

  const getCachedData = useCallback(() => {
    // If we have cached data with the same amount, use it
    const cachedData = getCachedDataStorage(totalDeductibleAmount);
    if (cachedData) {
      setData(cachedData);
    }
  }, [totalDeductibleAmount]);

  const setCachedData = useCallback(
    (content: string) => {
      const timestamp = new Date().toISOString();
      const cachedData = { content, totalDeductibleAmount, timestamp };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedData));
    },
    [totalDeductibleAmount],
  );

  const onDownloadProgress = useCallback((progress: any): void => {
    const chunks = progress.currentTarget.response?.split('\n') as string[];
    chunks.pop();
    if (!chunks?.[0]) return;

    const lastLine = last(chunks);
    const content = chunks
      .map((chuck: string) => JSON.parse(chuck)?.content)
      .join('');

    setStreamedData(content.split('\n'));

    if (lastLine?.includes('finish_reason')) {
      setCachedData(content);
      setData(content.split('\n'));
      setStreamedData([]);
      setStreamDone(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (consent !== AIUsageConsentType.allow || data?.length) return;

    try {
      setLoading(true);

      const message: ConversationMessage = {
        parts: [
          {
            type: 'text',
            content: t('ai_bot.suggested_question_what_should_be_deducted'),
          },
        ],
        content: t('ai_bot.suggested_question_what_should_be_deducted'),
        role: AIRoleEnum.user,
      };

      const payload = {
        ai_agent: AIAgentEnum.wrapped_advisor,
        message,
      } as ConversationQuery;

      await createConversation(payload, {
        timeout: 180 * ONE_SECOND,
        onDownloadProgress,
        responseType: 'stream',
      });
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Unknown error occurred'),
      );
    } finally {
      setLoading(false);
    }
  }, [consent, totalDeductibleAmount]);

  useEffect(() => {
    getCachedData();
  }, [getCachedData]);

  const fakeStream = useCallback(() => {
    let timeoutId: NodeJS.Timeout;

    setLoading(true);

    const animate = () => {
      if (fakeStreamWordIndexRef.current >= data.length) {
        fakeStreamWordIndexRef.current = 0;
        setStreamedData([]);
        setStreamDone(true);
        setLoading(false);
        return;
      }

      setStreamedData((prev) =>
        prev.concat([data[fakeStreamWordIndexRef.current]]),
      );
      fakeStreamWordIndexRef.current++;
      timeoutId = setTimeout(animate, 200);
    };

    timeoutId = setTimeout(animate, 200);

    return () => clearTimeout(timeoutId);
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || loading) return;
        if (!data?.length) return fetchData();
        if (!streamDone) return fakeStream();
      },
      { threshold: 0.1 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [data?.length, loading, fetchData, fakeStream, streamDone, containerRef]);

  return {
    data: streamDone ? data : streamedData,
    loading,
    error,
  };
};

export default useDudictibilitySuggestions;
